import type React from 'react';
import { useEffect } from 'react';

import { debounce, throttle } from 'lodash';

import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useXdpEmbeddedScroll } from 'bundles/unified-description-page-common/contexts/XdpEmbeddedScrollContext';

const useUpdateCtaPosition = (pageCtaRef: React.RefObject<HTMLDivElement>) => {
  const { updatePageCtaPosition, updateFixedPageNavHeight } = usePageNavigation();
  const embeddedScroll = useXdpEmbeddedScroll();

  // In embedded mode, set the threshold to the modal container's top offset so
  // showPageCtaInNav fires when the hero CTA scrolls above the modal viewport.
  useEffect(() => {
    if (!embeddedScroll || !updateFixedPageNavHeight) return;
    const containerTop = embeddedScroll.scrollContainerRef.current?.getBoundingClientRect().top ?? 0;
    updateFixedPageNavHeight(Math.max(containerTop, 1));
  }, [embeddedScroll, updateFixedPageNavHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateCtaPositionThrottle = throttle(() => {
      const bottom = pageCtaRef?.current?.getBoundingClientRect().bottom;
      if (bottom !== undefined) {
        updatePageCtaPosition?.(bottom);
      }
    }, 100);

    const updateCtaPositionDebounce = debounce(
      () => {
        const bottom = pageCtaRef?.current?.getBoundingClientRect().bottom;
        if (bottom !== undefined) {
          updatePageCtaPosition?.(bottom);
        }
      },
      300,
      { trailing: true }
    );

    const updateCtaPosition = () => {
      updateCtaPositionThrottle();
      updateCtaPositionDebounce();
    };

    updateCtaPosition();

    // In embedded mode, listen to the modal's scroll container; otherwise use window.
    const scrollTarget: EventTarget = embeddedScroll?.scrollContainerRef.current ?? window;
    scrollTarget.addEventListener('scroll', updateCtaPosition);
    window.addEventListener('resize', updateCtaPosition);

    return () => {
      scrollTarget.removeEventListener('scroll', updateCtaPosition);
      window.removeEventListener('resize', updateCtaPosition);
    };
  }, [pageCtaRef, updatePageCtaPosition, embeddedScroll]);
};

export default useUpdateCtaPosition;
